import React, { useEffect, useState } from 'react';
import { orderAPI, addressAPI } from '../../services/api';
import { getImgUrl } from '../../utils/config';
import { Loader, XCircle, MapPin } from 'lucide-react';

const Orders = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addresses, setAddresses] = useState([]);
    const [addressModalOpen, setAddressModalOpen] = useState(false);
    const [selectedAddressId, setSelectedAddressId] = useState(null);
    const [selectedOrderId, setSelectedOrderId] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    useEffect(() => {
        fetchOrders();
    }, []);

    const fetchOrders = async () => {
        try {
            const response = await orderAPI.getUserOrders();
            // Backend returns array directly, not {orders: [...]}
            setOrders(response.data || []);
        } catch (error) {
            console.error("Failed to fetch orders", error);
            setOrders([]);
        } finally {
            setLoading(false);
        }
    };

    const handleCancelOrder = async (orderId) => {
        if (window.confirm("Are you sure you want to cancel this order?")) {
            setActionLoading(true);
            try {
                await orderAPI.cancelOrder(orderId);
                fetchOrders(); // Refresh status
            } catch (error) {
                console.error("Failed to cancel order", error);
                alert("Failed to cancel order");
            } finally {
                setActionLoading(false);
            }
        }
    };

    const fetchAddresses = async () => {
        try {
            const response = await addressAPI.getAll();
            setAddresses(response.data.data || []);
        } catch (error) {
            console.error("Failed to fetch addresses", error);
        }
    };

    const handleChangeAddressClick = (order) => {
        fetchAddresses();
        setSelectedOrderId(order.id);
        setSelectedAddressId(order.address_id);
        setAddressModalOpen(true);
    };

    const handleUpdateAddress = async () => {
        if (!selectedAddressId || !selectedOrderId) return;
        setActionLoading(true);
        try {
            await orderAPI.updateAddress(selectedOrderId, selectedAddressId);
            setAddressModalOpen(false);
            fetchOrders(); // Refresh orders
        } catch (error) {
            console.error("Failed to update address", error);
            alert("Failed to update address");
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <div className="pt-24 min-h-screen bg-gray-50 px-6 pb-12">
            <div className="container mx-auto max-w-4xl">
                <h1 className="text-3xl font-bold text-gray-800 mb-8">My Orders</h1>

                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <Loader className="animate-spin text-green-600" size={48} />
                    </div>
                ) : orders.length === 0 ? (
                    <div className="bg-white rounded-xl shadow p-8 text-center">
                        <p className="text-gray-500 text-lg">You haven't placed any orders yet.</p>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {orders.map((order) => (
                            <div key={order.id} className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
                                <div className="bg-gray-50 px-6 py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-gray-200">
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <p className="text-sm text-gray-500">Order Placed</p>
                                            <p className="font-semibold text-gray-800">{new Date(order.createdAt).toLocaleDateString()}</p>
                                        </div>
                                        <div className="mt-2 text-xs text-gray-500">
                                            <p className="font-semibold text-gray-700">Shipping Address:</p>
                                            <p>{order.address?.address}, {order.address?.city} - {order.address?.pincode}</p>
                                            <p>Phone: {order.address?.mobile}</p>
                                        </div>
                                    </div>
                                    <div className="mt-2 sm:mt-0">
                                        <p className="text-sm text-gray-500">Total Amount</p>
                                        <p className="font-semibold text-gray-800">₹{order.totalAmount}</p>
                                        <p className="text-xs text-green-600 mt-1">Cash on Delivery</p>
                                    </div>
                                    <div className="mt-2 sm:mt-0">
                                        <p className="text-sm text-gray-500">Status</p>
                                        <span className={`px-3 py-1 text-xs font-bold rounded-full uppercase tracking-wide 
                                            ${order.status === 'completed' ? 'bg-green-100 text-green-800' :
                                                order.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                                                    order.status === 'shipped' ? 'bg-blue-100 text-blue-800' :
                                                        order.status === 'confirmed' ? 'bg-indigo-100 text-indigo-800' :
                                                            'bg-yellow-100 text-yellow-800'}`}>
                                            {order.status}
                                        </span>
                                    </div>
                                </div>
                                <div className="px-6 py-4">
                                    <ul className="divide-y divide-gray-100">
                                        {order.items?.map((item, idx) => (
                                            <li key={idx} className="py-4 flex items-center">
                                                <div className="h-16 w-16 flex-shrink-0 border border-gray-200 rounded-md overflow-hidden">
                                                    <img
                                                        src={getImgUrl(item.variation?.product?.image) || 'https://via.placeholder.com/64'}
                                                        alt={item.variation?.product?.name}
                                                        className="h-full w-full object-cover object-center"
                                                    />
                                                </div>
                                                <div className="ml-4 flex-1">
                                                    <p className="text-sm font-medium text-gray-900">{item.variation?.product?.name}</p>
                                                    <p className="text-sm text-gray-500">Quantity: {item.qty}</p>
                                                </div>
                                                <div className="text-sm font-medium text-gray-900">
                                                    ₹{item.totalPrice}
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="mt-4 flex justify-end gap-3">
                                        {order.status === 'Pending' && (
                                            <>
                                                <button
                                                    onClick={() => handleCancelOrder(order.id)}
                                                    disabled={actionLoading}
                                                    className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md shadow-sm text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                                >
                                                    <XCircle size={16} className="mr-2" /> Cancel Order
                                                </button>
                                                <button
                                                    onClick={() => handleChangeAddressClick(order)}
                                                    disabled={actionLoading}
                                                    className="inline-flex items-center px-4 py-2 border border-green-300 text-sm font-medium rounded-md shadow-sm text-green-700 bg-white hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                                >
                                                    <MapPin size={16} className="mr-2" /> Change Address
                                                </button>
                                            </>
                                        )}
                                        {order.status !== 'Cancelled' && (
                                            <a href={`/orders/${order.id}`} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">
                                                Track Order
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Change Address Modal */}
                {addressModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                        <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                                <h2 className="text-xl font-bold text-gray-800">Select New Address</h2>
                                <button onClick={() => setAddressModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                                    &times;
                                </button>
                            </div>
                            <div className="p-6">
                                <div className="space-y-3 mb-6">
                                    {addresses.map((addr) => (
                                        <div key={addr.id} className="flex items-start">
                                            <input
                                                id={`addr-${addr.id}`}
                                                name="address-selection"
                                                type="radio"
                                                checked={selectedAddressId === addr.id}
                                                onChange={() => setSelectedAddressId(addr.id)}
                                                className="focus:ring-green-500 h-4 w-4 text-green-600 border-gray-300 mt-1"
                                            />
                                            <label htmlFor={`addr-${addr.id}`} className="ml-3 block text-sm text-gray-700 cursor-pointer w-full">
                                                <span className="font-medium block">{addr.type || 'Home'} - {addr.name}</span>
                                                <span className="block text-gray-500">{addr.address}, {addr.city}, {addr.state} - {addr.pincode}</span>
                                            </label>
                                        </div>
                                    ))}
                                    {addresses.length === 0 && (
                                        <p className="text-gray-500 text-center">No other addresses found.</p>
                                    )}
                                </div>
                                <div className="flex justify-end gap-3">
                                    <button
                                        onClick={() => setAddressModalOpen(false)}
                                        className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-gray-700"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleUpdateAddress}
                                        disabled={actionLoading || !selectedAddressId}
                                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                                    >
                                        {actionLoading ? 'Updating...' : 'Update Address'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>

    );
};

export default Orders;
